import { MapState } from 'src/app/interfaces/map.interface';
import { SearchAggregationBucket, SearchNestedAggregation } from 'src/app/swagger-api';
import { SearchResultAggregation } from 'src/app/swagger-api/model/searchResultAggregation';
import { AggregationKey } from '../../map.interfaces';
import MapSettings from '../../map.settings';
import MarkersMappers from '../markers.mappers';
import { AggregationProcessorType, SharedResponseProcessorDecorator } from './shared-response-processor.decorator';

export class AggregationResponseProcessorDecorator extends SharedResponseProcessorDecorator<SearchResultAggregation> {
  public constructor(entity: AggregationProcessorType, state: MapState) {
    super(entity, state);
  }

  public getProcessedItems(): SearchResultAggregation[] {
    let results: SearchResultAggregation[] = [];
    this.entity.getProcessedItems().forEach((searchResultAggregation: SearchResultAggregation) => {
      searchResultAggregation.buckets.forEach((bucket: SearchAggregationBucket) => {
        results = this.processAggregationBucket(bucket, results, searchResultAggregation);
      });
    });
    return results;
  }

  private processAggregationBucket(
    bucket: SearchAggregationBucket,
    results: SearchResultAggregation[],
    searchResultAggregation: SearchResultAggregation,
  ): SearchResultAggregation[] {
    const nestedAggregations: SearchNestedAggregation[] = this.filterOutIndividualsIfOtherAggregationsPresent(
      bucket.nested_aggregations,
    );
    nestedAggregations.forEach((nestedAggregation: SearchNestedAggregation) => {
      if (nestedAggregation.nested_buckets.length > 0) {
        nestedAggregation.nested_buckets.forEach((nestedBucket: SearchAggregationBucket) => {
          results = this.pushNestedAggregation({ bucket, nestedBucket, results, searchResultAggregation });
        });
      }
    });
    return results;
  }

  private pushNestedAggregation(data: {
    bucket: SearchAggregationBucket;
    nestedBucket: SearchAggregationBucket;
    results: SearchResultAggregation[];
    searchResultAggregation: SearchResultAggregation;
  }): SearchResultAggregation[] {
    if (data.nestedBucket.aggregated_items_count && !data.nestedBucket.nested_aggregations) {
      data.results.push(
        this.getNestedAggregation(
          data.searchResultAggregation,
          data.nestedBucket,
          data.bucket.aggregation_key as AggregationKey,
        ),
      );
    }
    if (data.nestedBucket.nested_aggregations && data.nestedBucket.nested_aggregations.length > 0) {
      const nestedAggregations: SearchNestedAggregation[] = this.filterOutIndividualsIfOtherAggregationsPresent(
        data.nestedBucket.nested_aggregations,
      );

      nestedAggregations.forEach((nestedAggregation: SearchNestedAggregation) => {
        nestedAggregation.nested_buckets.forEach((nestedBucket: SearchAggregationBucket) => {
          if (nestedBucket.aggregated_items_count && !nestedBucket.nested_aggregations) {
            data.results.push(
              this.getNestedAggregation(
                data.searchResultAggregation,
                nestedBucket,
                data.nestedBucket.aggregation_key as AggregationKey,
              ),
            );
          }
          if (nestedBucket.nested_aggregations && nestedBucket.nested_aggregations.length > 0) {
            this.processAggregationBucket(nestedBucket, data.results, data.searchResultAggregation);
          }
        });
      });
    }
    return data.results;
  }

  private filterOutIndividualsIfOtherAggregationsPresent(
    nestedAggregations: SearchNestedAggregation[],
  ): SearchNestedAggregation[] {
    const nonEmptyNestedAggregations: SearchNestedAggregation[] = nestedAggregations.filter(
      (e: SearchNestedAggregation) => e.nested_buckets.length > 0,
    );
    return nonEmptyNestedAggregations.length > 1
      ? nonEmptyNestedAggregations.filter(
          (b: SearchNestedAggregation) => b.nested_bucket_name !== MapSettings.PEOPLE_TAB_NESTED_AGGREGATE_ATTR_NAME,
        )
      : nonEmptyNestedAggregations;
  }

  private getNestedAggregation(
    result: SearchResultAggregation,
    nestedBucket: SearchAggregationBucket,
    aggregationKey: AggregationKey,
  ): SearchResultAggregation {
    return {
      aggregated_entities: [
        MarkersMappers.nestedBucketIndividualNameToSearchCategoryMapper[
          (nestedBucket.aggregation_key as AggregationKey).name || result.aggregated_entities[0]
        ],
      ] as string[],
      bucket_name: result.bucket_name,
      buckets: [
        {
          aggregated_items_count: nestedBucket.aggregated_items_count,
          aggregation_key: aggregationKey,
        },
      ],
    };
  }
}
